import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';

export interface LegalFileMeta {
  lang: string;      // e.g. 'ENG' or 'GER'
  version: string;   // e.g. 'V1_28.09.2025'
  filename: string;  // relative filename (under assets/legal/{sectionKey}/)
}

export interface LegalSection {
  key: string;
  title: string;
  files: LegalFileMeta[];
  translationKey: string;
}

/**
 * IMPORTANT:
 * Place .html files under: src/assets/legal/{sectionKey}/{filename}.html
 * Example: src/assets/legal/terms-and-conditions/terms_eng_v1_28-09-2025.html
 */
@Injectable({
  providedIn: 'root'
})
export class LegalContentService {
  private assetsBase = '/assets/legal';

  // configure sections, languages and versions here
  private sections: Record<string, LegalSection> = {
    'terms-and-conditions': {
      key: 'terms-and-conditions',
      title: 'Terms & Conditions',
      translationKey: 'legal_side_nav_title_terms_conditions',
      files: [
        { lang: 'ENG', version: 'V1_28.09.2025', filename: 'terms_eng_v1_28-09-2025.html' },
        { lang: 'GER', version: 'V1_28.09.2025', filename: 'agb_ger_v1_28-09-2025.html' }
      ]
    },
    'cookies': {
      key: 'cookies',
      title: 'Cookies',
        translationKey: 'legal_side_nav_title_terms_cookies',
      files: [
        { lang: 'ENG', version: 'V1_28.09.2025', filename: 'cookie_eng_v1_28-09-2025.html' },
        { lang: 'GER', version: 'V1_28.09.2025', filename: 'cookie_ger_v1_28-09-2025.html' }
      ]
    },
    'privacy-policy': {
      key: 'privacy-policy',
      title: 'Privacy Policy',
      translationKey: 'legal_side_nav_title_privacy_policy',
      files: [
        { lang: 'ENG', version: 'V1_28.09.2025', filename: 'privacy_eng_v1_28-09-2025.html' },
        { lang: 'GER', version: 'V1_28.09.2025', filename: 'privacy_ger_v1_28-09-2025.html' }
      ]
    },
    'accessibility': {
      key: 'accessibility',
      title: 'Accessibility',
      translationKey: 'legal_side_nav_title_accessibility',
      files: [
        { lang: 'ENG', version: 'V1_28.09.2025', filename: 'accessibility_eng_v1_28-09-2025.html' },
        { lang: 'GER', version: 'V1_28.09.2025', filename: 'accessibility_ger_v1_28-09-2025.html' } // added GER
      ]
    },
    'impressum': {
      key: 'impressum',
      title: 'Impressum',
      translationKey: 'legal_side_nav_title_impressum',
      files: [
        { lang: 'GER', version: 'V1_28.09.2025', filename: 'impressum_ger_v1_28-09-2025.html' },
        { lang: 'ENG', version: 'V1_28.09.2025', filename: 'impressum_eng_v1_28-09-2025.html' } // added ENG
      ]
    },
    
  };

  constructor(private http: HttpClient) {}

  getSectionsList(): LegalSection[] {
    return Object.values(this.sections);
  }

  getSection(key: string): LegalSection | undefined {
    return this.sections[key];
  }

  getFilesForSection(key: string): LegalFileMeta[] {
    return this.sections[key]?.files || [];
  }

  fetchFileContent(sectionKey: string, filename: string): Observable<string> {
    const url = `${this.assetsBase}/${sectionKey}/${filename}`;
    return this.http.get(url, { responseType: 'text' });
  }

  // helper to add/extend entries at runtime if needed
  addFileToSection(sectionKey: string, file: LegalFileMeta) {
    const s = this.sections[sectionKey];
    if (s) {
      s.files.push(file);
    }
  }

  // ---- NEW helpers for shareable slugs and resolving ----

  // slug for a file: filename without .html (safe to use in routes)
  fileToSlug(file: LegalFileMeta): string {
    return file.filename.replace(/\.html$/i, '');
  }

  // build a relative share url for a file (e.g. /legal/terms-and-conditions/terms_eng_v1_28-09-2025)
  buildShareUrl(sectionKey: string, file: LegalFileMeta): string {
    const slug = this.fileToSlug(file);
    return `/legal/${sectionKey}/${encodeURIComponent(slug)}`;
  }

  // find file by slug (slug = filename without extension)
  getFileBySlug(sectionKey: string, slug: string): LegalFileMeta | undefined {
    const files = this.getFilesForSection(sectionKey);
    if (!files) return undefined;
    const decoded = decodeURIComponent(slug || '');
    return files.find(f => this.fileToSlug(f) === decoded || f.filename === decoded);
  }
}
